/* ═══════════════════════════════════════════════════════════════════════════
   pw-pricing-engine.js
   ─────────────────────────────────────────────────────────────────────────
   Single source of truth for Printwear pricing math.

   Used by:
     • PDP (apparel) via pw-tier-pricing.liquid
     • PDP (hard goods) via pw-hg-tier-pricing.liquid
     • Quote Builder page (this project)
     • Cloudflare Worker server-side validation (port to Worker runtime)

   If a calculated price differs between any of those surfaces, this file
   is the bug — fix here, deploy once, all surfaces update.

   Reference:  brief.md §5.4
   Owner:      Inksie Pty Ltd (ABN 78 654 721 070)
   ═══════════════════════════════════════════════════════════════════════════ */

(function (root) {
  'use strict';

  /* ── Version ────────────────────────────────────────────────────────────
     Bump on ANY change to: rates, MOQs, tier brackets, setup math, underbase
     rules. Saved quotes record the version they were priced under; reload
     re-prices and warns the buyer if VERSION has moved on.
  ─────────────────────────────────────────────────────────────────────────── */
  var VERSION = '1.0.0';

  /* ── Apparel decoration rates ───────────────────────────────────────────
     Ported from pw-tier-pricing.liquid. Treat as canonical.

     Schema:
       method → {
         impression: { colour_bracket → { qty_bracket → rate_aud_ex_gst } }
         setup:      { new, repeat } | null
         setupPerUnit: 'colour' | 'flat' | undefined
       }

     Colour brackets (screen-printing only):
       1 = 1 colour, 2 = 2-3 colours, 4 = 4+ colours
     For DTG/DTF, `colours` is repurposed as front/back placement code:
       1 = front only, 2 = front+back

     Qty brackets:
       Screen Print: 25-49, 50-99, 100-249, 250+        (MOQ 25)
       Embroidery:   10-19, 20-34, 35-49, 50-99, 100+   (MOQ 10)
       DTG / DTF:    flat 'any' rate                    (MOQ 10)

     Setup math:
       setupPerUnit 'colour' → setup × (colour_count + (underbase ? 1 : 0))
       setupPerUnit 'flat'   → setup once
       setup null            → no setup (DTG, DTF)
  ─────────────────────────────────────────────────────────────────────────── */
  var DECO_RATES = {
    'screen-printing': {
      impression: {
        1: { '25-49': 5.50, '50-99': 4.00, '100-249': 3.00, '250+': 2.50 },
        2: { '25-49': 7.50, '50-99': 5.50, '100-249': 4.00, '250+': 3.25 },
        4: { '25-49':10.00, '50-99': 7.50, '100-249': 5.50, '250+': 4.50 }
      },
      setup:        { new: 60, repeat: 30 },
      setupPerUnit: 'colour'
    },
    'embroidery': {
      impression: {
        1: { '10-19': 8.00, '20-34': 6.50, '35-49': 5.50, '50-99': 5.00, '100+': 4.00 }
      },
      setup:        { new: 60, repeat: 30 },
      setupPerUnit: 'flat'
    },
    'dtg': {
      impression: {
        1: { 'any': 10.00 },
        2: { 'any': 15.00 }
      },
      setup: null
    },
    'dtf': {
      impression: {
        1: { 'any': 10.00 },
        2: { 'any': 15.00 }
      },
      setup: null
    }
  };

  /* Method-level MOQ. Effective MOQ = max(method MOQ, product.custom.moq). */
  var METHOD_MOQ = {
    'screen-printing': 25,
    'embroidery':      10,
    'dtg':             10,
    'dtf':             10
  };

  /* Apparel quantity tiers — drives variant lookup (option2 = tier). */
  var APPAREL_TIERS = [
    { min: 1,  max: 9,        key: '1-9',   label: '1\u20139' },
    { min: 10, max: 49,       key: '10-49', label: '10\u201349' },
    { min: 50, max: Infinity, key: '50+',   label: '50+' }
  ];

  /* Display-name → engine-key map. Catalogue stores method names like
     "Screen Printing" / "Embroidery"; engine uses kebab-case keys. */
  var METHOD_KEY_MAP = {
    'Screen Printing':   'screen-printing',
    'Screen Print':      'screen-printing',
    'Screenprint':       'screen-printing',
    'Embroidery':        'embroidery',
    'DTG':               'dtg',
    'Direct to Garment': 'dtg',
    'DTF':               'dtf',
    'Direct to Film':    'dtf'
  };

  function methodLabelToKey(label) {
    if (!label) return null;
    return METHOD_KEY_MAP[label] || null;
  }

  /* ── Math primitives ──────────────────────────────────────────────────── */
  function round2(n) { return Math.round(n * 100) / 100; }

  function formatAUD(n) {
    n = (typeof n === 'number' && !isNaN(n)) ? n : 0;
    var sign = n < 0 ? '-' : '';
    var abs = Math.abs(n);
    return sign + '$' + abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ═══════════════════════════════════════════════════════════════════════
     APPAREL
     ═══════════════════════════════════════════════════════════════════════ */

  function getApparelTier(qty) {
    var n = (typeof qty === 'number' && qty > 0) ? qty : 1;
    for (var i = 0; i < APPAREL_TIERS.length; i++) {
      if (n >= APPAREL_TIERS[i].min && n <= APPAREL_TIERS[i].max) {
        return APPAREL_TIERS[i];
      }
    }
    return APPAREL_TIERS[0];
  }

  function getApparelImpressionBracket(method, totalQty) {
    var key = methodLabelToKey(method) || method;
    if (key === 'dtg' || key === 'dtf') return 'any';

    if (key === 'screen-printing') {
      if (totalQty >= 250) return '250+';
      if (totalQty >= 100) return '100-249';
      if (totalQty >= 50)  return '50-99';
      return '25-49';
    }

    if (key === 'embroidery') {
      if (totalQty >= 100) return '100+';
      if (totalQty >= 50)  return '50-99';
      if (totalQty >= 35)  return '35-49';
      if (totalQty >= 20)  return '20-34';
      return '10-19';
    }

    return null;
  }

  /**
   * Per-unit decoration impression rate (AUD ex GST).
   *
   * @param {string}  method   Display name or kebab key
   * @param {number}  colours  Screen: colour count; DTG/DTF: 1=front, 2=front+back
   * @param {number}  totalQty Sum across all sizes
   * @returns {number}
   */
  function getApparelImpressionRate(method, colours, totalQty) {
    var key = methodLabelToKey(method) || method;
    var rates = DECO_RATES[key];
    if (!rates) return 0;

    /* DTG/DTF: 'colours' parameter is placement count (1 = front, 2 = both) */
    if (key === 'dtg' || key === 'dtf') {
      var dtgKey = (colours >= 2) ? 2 : 1;
      var dtgBracket = rates.impression[dtgKey];
      return (dtgBracket && dtgBracket['any']) || 0;
    }

    var colourKey = (key === 'screen-printing')
      ? (colours >= 4 ? 4 : (colours >= 2 ? 2 : 1))
      : 1;

    var tierRates = rates.impression[colourKey];
    if (!tierRates) return 0;

    var bracket = getApparelImpressionBracket(method, totalQty);
    return tierRates[bracket] || 0;
  }

  /**
   * One-time decoration setup (AUD ex GST).
   *
   * @param {string}  method
   * @param {number}  colours    Screen-print colour count; ignored for others
   * @param {boolean} repeat     Reorder — screens already exist (half setup)
   * @param {boolean} underbase  Screen print only — adds +1 screen on dark garments
   * @returns {number}
   */
  function getApparelSetupFee(method, colours, repeat, underbase) {
    var key = methodLabelToKey(method) || method;
    var rates = DECO_RATES[key];
    if (!rates || !rates.setup) return 0;

    var baseSetup = repeat ? rates.setup.repeat : rates.setup.new;

    if (rates.setupPerUnit === 'colour') {
      var c = (typeof colours === 'number' && colours > 0) ? colours : 1;
      var screens = c + (underbase ? 1 : 0);
      return baseSetup * screens;
    }

    return baseSetup;
  }

  /**
   * Determine if a garment colour requires an underbase screen.
   * Whites and very pale colours = no underbase. Everything else = yes.
   */
  function needsUnderbase(colourName) {
    if (!colourName) return true;
    var lower = String(colourName).toLowerCase().trim();
    var noUnderbase = ['white','natural','cream','ivory','oat','oatmeal','off-white','off white'];
    return noUnderbase.indexOf(lower) === -1;
  }

  function getMethodMOQ(method) {
    var key = methodLabelToKey(method) || method;
    return METHOD_MOQ[key] || 0;
  }

  function getApparelEffectiveMOQ(method, productMOQ) {
    var methodMOQVal = getMethodMOQ(method);
    var prodMOQ = (typeof productMOQ === 'number' && productMOQ > 0) ? productMOQ : 0;
    return Math.max(methodMOQVal, prodMOQ);
  }

  /**
   * Look up unit garment price from the apparel tier prices map.
   * Falls back gracefully if a tier key isn't populated.
   *
   * @param {object} tierPrices  e.g. { '1-9': 19.95, '10-49': 17.50, '50+': 14.99 }
   * @param {number} qty
   * @returns {{tier, price}}
   */
  function getApparelTierPrice(tierPrices, qty) {
    var prices = tierPrices || {};
    var tier = getApparelTier(qty);

    if (prices[tier.key] != null) return { tier: tier, price: prices[tier.key] };

    /* Fallbacks for partially-populated price maps. */
    if (tier.key === '50+'   && prices['10-49'] != null) return { tier: tier, price: prices['10-49'] };
    if (tier.key === '10-49' && prices['50+']   != null) return { tier: tier, price: prices['50+']   };
    if (tier.key === '10-49' && prices['1-9']   != null) return { tier: tier, price: prices['1-9']   };
    if (prices['1-9']   != null) return { tier: tier, price: prices['1-9']   };
    if (prices['10-49'] != null) return { tier: tier, price: prices['10-49'] };
    if (prices['50+']   != null) return { tier: tier, price: prices['50+']   };

    return { tier: tier, price: 0 };
  }

  /**
   * Calculate full apparel line subtotal.
   *
   * @param {object} input
   * @param {object} input.tierPrices       { '1-9', '10-49', '50+' } prices map
   * @param {object} [input.size_breakdown] { 'XS': 5, 'S': 12, ... } or null in soft mode
   * @param {number} [input.soft_qty]       fallback total qty when no breakdown
   * @param {number} [input.product_moq]    custom.moq metafield value
   * @param {object} input.deco             { method, colours, repeat, underbase, front_back }
   * @returns {object} full breakdown
   */
  function calculateApparelLine(input) {
    var totalQty = 0;
    if (input.size_breakdown) {
      for (var s in input.size_breakdown) {
        if (Object.prototype.hasOwnProperty.call(input.size_breakdown, s)) {
          totalQty += parseInt(input.size_breakdown[s], 10) || 0;
        }
      }
    }
    if (totalQty === 0 && typeof input.soft_qty === 'number') totalQty = input.soft_qty;

    var tp = getApparelTierPrice(input.tierPrices, totalQty);
    var unitGarment = tp.price;
    var garmentSubtotal = round2(unitGarment * totalQty);

    var deco = input.deco || {};
    var sides = (deco.front_back === 'front+back') ? 2 : 1;

    /* DTG/DTF: the impression rate is keyed by placement count (1=front-only,
     * 2=front+back) and is already a per-garment all-in price. Selecting key 2
     * already factors in both sides — we must NOT then multiply by `sides`,
     * or front+back gets double-counted. SP/Em do scale linearly with sides
     * (separate screens, separate per-side rates), so they keep the multiplier. */
    var methodKey = methodLabelToKey(deco.method) || deco.method;
    var isDtgDtf  = (methodKey === 'dtg' || methodKey === 'dtf');
    var rateColours = isDtgDtf ? sides : deco.colours;
    var decoSidesMultiplier = isDtgDtf ? 1 : sides;

    var rate = getApparelImpressionRate(deco.method, rateColours, totalQty);
    var decoSubtotal = round2(rate * totalQty * decoSidesMultiplier);
    var decoQtyBracket = getApparelImpressionBracket(deco.method, totalQty);

    var setupOneSide = getApparelSetupFee(deco.method, deco.colours, deco.repeat, deco.underbase);
    var setupTotal = round2(setupOneSide * sides);

    var moq = getApparelEffectiveMOQ(deco.method, input.product_moq);
    var moqOk = totalQty >= moq;

    return {
      total_qty:           totalQty,
      tier:                tp.tier,
      unit_garment_price:  unitGarment,
      garment_subtotal:    garmentSubtotal,
      deco_rate:           rate,
      deco_subtotal:       decoSubtotal,
      deco_qty_bracket:    decoQtyBracket,
      setup:               setupTotal,
      sides:               sides,
      moq:                 moq,
      moq_ok:              moqOk,
      line_total:          round2(garmentSubtotal + decoSubtotal + setupTotal),
      pricing_engine_version: VERSION
    };
  }

  /* ═══════════════════════════════════════════════════════════════════════
     HARD GOODS
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Look up unit sell price from price_tiers JSON metafield.
   * @param {Array}  priceTiers  parsed custom.price_tiers JSON
   * @param {number} qty
   */
  function getHardGoodsTierPrice(priceTiers, qty) {
    if (!priceTiers || !priceTiers.length) return null;

    var sorted = priceTiers.slice().sort(function (a, b) {
      return (a.qty || 0) - (b.qty || 0);
    });

    var n = (typeof qty === 'number' && qty > 0) ? qty : 1;
    var match = sorted[0];
    for (var i = 0; i < sorted.length; i++) {
      if (n >= (sorted[i].qty || 0)) match = sorted[i];
    }

    var basePrice = sorted[0].sell || 0;
    var matchSell = match.sell || 0;
    var savings = basePrice > 0
      ? round2(((basePrice - matchSell) / basePrice) * 100)
      : 0;

    return {
      tier_qty:        match.qty,
      unit_sell:       matchSell,
      unit_cost:       match.cost || 0,
      savings_vs_base: savings
    };
  }

  /**
   * Decoration cost from a custom.decoration_options DO entry.
   * @param {object} decoOption  { type, method, area, unit_sell, setup_sell, ... }
   * @param {number} qty
   */
  function calculateHardGoodsDecoration(decoOption, qty) {
    if (!decoOption) return { unit_sell: 0, setup_sell: 0, deco_subtotal: 0 };

    var unitSell  = (typeof decoOption.unit_sell  === 'number') ? decoOption.unit_sell  : 0;
    var setupSell = (typeof decoOption.setup_sell === 'number') ? decoOption.setup_sell : 0;
    var n = (typeof qty === 'number' && qty > 0) ? qty : 0;

    return {
      unit_sell:     unitSell,
      setup_sell:    round2(setupSell),
      deco_subtotal: round2(unitSell * n)
    };
  }

  /**
   * Calculate full hard-goods line subtotal.
   *
   * @param {object} input
   * @param {Array}  input.priceTiers     parsed custom.price_tiers
   * @param {object} input.decoOption     chosen DO entry
   * @param {number} input.qty
   * @param {number} [input.product_moq]
   * @param {Array}  [input.surcharges]   [{ desc, unit_sell, setup_sell? }]
   */
  function calculateHardGoodsLine(input) {
    var qty = (typeof input.qty === 'number' && input.qty > 0) ? input.qty : 0;
    var tier = getHardGoodsTierPrice(input.priceTiers || [], qty);
    var unitSell = tier ? tier.unit_sell : 0;
    var garmentSubtotal = round2(unitSell * qty);

    var decoCalc = calculateHardGoodsDecoration(input.decoOption, qty);

    var surchargeSubtotal = 0;
    if (Array.isArray(input.surcharges)) {
      input.surcharges.forEach(function (s) {
        if (s && typeof s.unit_sell === 'number') surchargeSubtotal += s.unit_sell * qty;
        if (s && typeof s.setup_sell === 'number') surchargeSubtotal += s.setup_sell;
      });
    }
    surchargeSubtotal = round2(surchargeSubtotal);

    /* MOQ is the higher of: product custom.moq, first tier qty. */
    var firstTierQty = (input.priceTiers && input.priceTiers[0])
      ? (input.priceTiers[0].qty || 0)
      : 0;
    var moq = Math.max(input.product_moq || 0, firstTierQty);
    var moqOk = qty >= moq;

    return {
      qty:                    qty,
      tier:                   tier,
      unit_sell:              unitSell,
      garment_subtotal:       garmentSubtotal,
      deco_unit_sell:         decoCalc.unit_sell,
      deco_subtotal:          decoCalc.deco_subtotal,
      setup:                  decoCalc.setup_sell,
      surcharge_subtotal:     surchargeSubtotal,
      moq:                    moq,
      moq_ok:                 moqOk,
      line_total:             round2(garmentSubtotal + decoCalc.deco_subtotal + decoCalc.setup_sell + surchargeSubtotal),
      pricing_engine_version: VERSION
    };
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SIZE ORDERING
     ═══════════════════════════════════════════════════════════════════════
     Apparel sizes ship from Shopify in arbitrary order ("2XL, L, M, S, XL").
     This comparator returns them in human-readable order:
        XXS, XS, S, M, L, XL, 2XL, 3XL, 4XL, 5XL, 6XL, 7XL, 8XL
     With fallthrough handling for kid/numeric sizing and "One Size".
  ─────────────────────────────────────────────────────────────────────────── */
  var SIZE_ORDER = {
    'XXS': 1, 'XS': 2, 'S': 3, 'M': 4, 'L': 5, 'XL': 6,
    '2XL': 7, 'XXL': 7, '3XL': 8, 'XXXL': 8, '4XL': 9, 'XXXXL': 9,
    '5XL': 10, '6XL': 11, '7XL': 12, '8XL': 13,
    'One Size': 99, 'OS': 99, 'O/S': 99
  };

  function compareSizes(a, b) {
    var ao = SIZE_ORDER[a];
    var bo = SIZE_ORDER[b];
    /* Both known apparel sizes */
    if (ao != null && bo != null) return ao - bo;
    /* Pure-numeric sizes (e.g. kids 2, 4, 6, 8 / women's 6-22 / shoe 5-13) */
    var an = parseFloat(a);
    var bn = parseFloat(b);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    /* Mixed: known apparel size beats numeric beats string */
    if (ao != null) return -1;
    if (bo != null) return 1;
    if (!isNaN(an)) return -1;
    if (!isNaN(bn)) return 1;
    return String(a).localeCompare(String(b));
  }

  function sortSizes(arr) {
    return (arr || []).slice().sort(compareSizes);
  }

  /* ═══════════════════════════════════════════════════════════════════════
     MODIFIER HOOK (extensibility — see brief §10.3)
     Reserved slot for future flat fees / percentage uplifts. Not used at
     launch; included so future additions don't require re-architecting.
     ═══════════════════════════════════════════════════════════════════════ */
  var registeredModifiers = [];

  function addModifier(modifier) {
    if (!modifier || !modifier.id) return;
    registeredModifiers = registeredModifiers.filter(function (m) { return m.id !== modifier.id; });
    registeredModifiers.push(modifier);
  }
  function listModifiers() { return registeredModifiers.slice(); }
  function clearModifiers() { registeredModifiers = []; }

  /* ═══════════════════════════════════════════════════════════════════════
     PUBLIC API
     ═══════════════════════════════════════════════════════════════════════ */
  var api = {
    VERSION:       VERSION,
    DECO_RATES:    DECO_RATES,
    METHOD_MOQ:    METHOD_MOQ,
    APPAREL_TIERS: APPAREL_TIERS,

    /* Apparel */
    getApparelTier:              getApparelTier,
    getApparelImpressionBracket: getApparelImpressionBracket,
    getApparelImpressionRate:    getApparelImpressionRate,
    getApparelSetupFee:          getApparelSetupFee,
    getApparelTierPrice:         getApparelTierPrice,
    getApparelEffectiveMOQ:      getApparelEffectiveMOQ,
    getMethodMOQ:                getMethodMOQ,
    needsUnderbase:              needsUnderbase,
    calculateApparelLine:        calculateApparelLine,

    /* Hard goods */
    getHardGoodsTierPrice:        getHardGoodsTierPrice,
    calculateHardGoodsDecoration: calculateHardGoodsDecoration,
    calculateHardGoodsLine:       calculateHardGoodsLine,

    /* Helpers */
    methodLabelToKey: methodLabelToKey,
    compareSizes:     compareSizes,
    sortSizes:        sortSizes,

    /* Modifiers */
    addModifier:    addModifier,
    listModifiers:  listModifiers,
    clearModifiers: clearModifiers,

    /* Formatting */
    formatAUD: formatAUD,
    round2:    round2
  };

  if (typeof window !== 'undefined') root.pwPricing = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

})(typeof window !== 'undefined' ? window : globalThis);
